import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
'use es6';
import { Component } from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { FormattedMessage } from 'LearningCenterUI/components/I18n';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import Exam from 'academy-shared-js/records/Exam';
import Lesson from 'academy-shared-js/records/Lesson';
import Video from 'academy-shared-js/records/Video';
import ExpandedLessonNavigatorLessonContainer from '../../../containers/Navigator/LessonNavigator/ExpandedLessonNavigatorLessonContainer';
import { TRACK_COMPLETE_URL_FRAGMENT, TRACK_INTRO_URL_FRAGMENT, TRACK_EXAM_URL_FRAGMENT, TRACK_RECOMMENDATIONS_URL_FRAGMENT } from '../../../constants/Navigation';
import ExpandedTrackNavigatorTopLevel from './ExpandedTrackNavigatorTopLevel';
import { calculateExamDurationSeconds } from '../../../utils/ContentUtils';
import { getTrackExamPageUrl, getTrackIntroPageUrl, getTrackRecommendationsUrl, getTrackWrapUpPageUrl } from '../../../utils/RoutingUtils';
import { getHumanizedDuration } from '../../../utils/utils';
import { getServerSyncedTime } from '../../../utils/TimeUtils';
import TrackViewAwardLink from '../../../components/TrackCard/TrackViewAwardLink';
import UIList from 'UIComponents/list/UIList';
import { useTrackAwardProgress, useTrackProgressById } from '../../../features/userProgress';
import { useMostRecentExamAttempt } from '../../../features/exams';
import ExamTimer from '../../../features/exams/components/ExamTimer';
import { HasParentPlaylist, NextPlaylistContent } from '../../../features/playlistContent';
const BASE_CLASS_NAME = 'track-card-simple';
export const ExpandedTrackNavigatorComponent = ({
  currentContentId,
  exam,
  lessons,
  track,
  trackIntroductionVideo
}) => {
  const {
    award,
    allProgressLoading
  } = useTrackAwardProgress(Number(track.get('id')));
  const {
    trackProgressByIdLoading
  } = useTrackProgressById(Number(track.get('id')));
  const {
    mostRecentExamAttempt,
    mostRecentExamAttemptLoading
  } = useMostRecentExamAttempt(Number(track.get('id')));

  if (!lessons || allProgressLoading || mostRecentExamAttemptLoading || trackProgressByIdLoading) {
    return /*#__PURE__*/_jsx(UILoadingSpinner, {
      grow: true
    });
  }

  const introDuration = trackIntroductionVideo && Math.max(0, trackIntroductionVideo.get('duration'));
  const examDuration = exam && Math.max(0, calculateExamDurationSeconds(exam.get('length')));
  const examIsRecent = mostRecentExamAttempt && getServerSyncedTime() - mostRecentExamAttempt.attemptFinishTime < 24 * 60 * 60 * 1000;
  const certificationExpired = !examIsRecent && award && award.isExpired;
  const totalQuestionCount = exam && exam.get('length');
  const correctQuestionCount = mostRecentExamAttempt && mostRecentExamAttempt.score;
  return /*#__PURE__*/_jsxs("div", {
    className: "p-bottom-15 m-bottom-6",
    "data-test-id": "expanded-track-navigator",
    children: [!!trackIntroductionVideo && /*#__PURE__*/_jsx(ExpandedTrackNavigatorTopLevel, {
      "data-test-id": "expanded-track-navigator-introduction",
      heading: trackIntroductionVideo.get('name'),
      isSelected: currentContentId === TRACK_INTRO_URL_FRAGMENT,
      subheading: getHumanizedDuration(introDuration),
      url: getTrackIntroPageUrl(track.get('id'))
    }), lessons.map(lesson => !!lesson && /*#__PURE__*/_jsx(ExpandedLessonNavigatorLessonContainer, {
      currentContentId: currentContentId,
      includeCompleteIcon: true,
      lesson: lesson,
      urlPrefix: `tracks/${track.get('id')}`
    }, lesson.get('id'))), !!exam && /*#__PURE__*/_jsx(ExpandedTrackNavigatorTopLevel, {
      "data-test-id": "expanded-track-navigator-exam",
      heading: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.exams.exam"
      }),
      isComplete: exam.get('completed'),
      isSelected: currentContentId === TRACK_EXAM_URL_FRAGMENT,
      subheading: mostRecentExamAttempt && mostRecentExamAttempt.passed && !certificationExpired ? /*#__PURE__*/_jsxs(UIList, {
        use: "inline-divided",
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.exams.finished.score",
          options: {
            correctQuestionCount,
            totalQuestionCount
          }
        }), /*#__PURE__*/_jsx(TrackViewAwardLink, {
          award: award,
          certification: track.get('certification'),
          className: `${BASE_CLASS_NAME}__view-cert`,
          size: 'small',
          trackId: track.get('id'),
          useButton: false
        })]
      }) : mostRecentExamAttempt && !mostRecentExamAttempt.passed && mostRecentExamAttempt.finished ? /*#__PURE__*/_jsxs(UIList, {
        use: "inline-divided",
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.exams.finished.score",
          options: {
            correctQuestionCount,
            totalQuestionCount
          }
        }), /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.exams.finished.failed"
        })]
      }) : mostRecentExamAttempt && mostRecentExamAttempt.passed && certificationExpired ? /*#__PURE__*/_jsxs(UIList, {
        use: "inline-divided",
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.exams.finished.expired"
        }), /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.exams.finished.failed"
        })]
      }) : mostRecentExamAttempt && !mostRecentExamAttempt.passed ? /*#__PURE__*/_jsx(ExamTimer, {
        passive: true,
        trackId: Number(track.get('id'))
      }) : /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.exams.navigation.duration",
        options: {
          duration: getHumanizedDuration(examDuration)
        }
      }),
      url: getTrackExamPageUrl(track.get('id'))
    }), !exam && /*#__PURE__*/_jsx(ExpandedTrackNavigatorTopLevel, {
      "data-test-id": "expanded-track-navigator-complete",
      heading: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.tracks.progress.heading"
      }),
      isComplete: false,
      isSelected: currentContentId === TRACK_COMPLETE_URL_FRAGMENT,
      url: getTrackWrapUpPageUrl(track.get('id'))
    }), /*#__PURE__*/_jsx(HasParentPlaylist, {
      fallback: /*#__PURE__*/_jsx(ExpandedTrackNavigatorTopLevel, {
        "data-test-id": "expanded-track-navigator-recommendations",
        heading: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.tracks.recommendations.navigation.heading"
        }),
        isSelected: currentContentId === TRACK_RECOMMENDATIONS_URL_FRAGMENT,
        subheading: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.tracks.recommendations.navigation.subheading"
        }),
        url: getTrackRecommendationsUrl(track.get('id'))
      }),
      children: /*#__PURE__*/_jsx(NextPlaylistContent, {
        render: ({
          title,
          subtitle,
          url,
          urlFragments
        }) => /*#__PURE__*/_jsx(ExpandedTrackNavigatorTopLevel, {
          "data-test-id": "expanded-track-navigator-next-playlist-content",
          heading: title,
          isSelected: urlFragments.includes(currentContentId),
          subheading: subtitle,
          url: url
        }),
        trackId: track.get('id') && Number(track.get('id'))
      })
    })]
  });
};
ExpandedTrackNavigatorComponent.propTypes = {
  currentContentId: PropTypes.string,
  exam: PropTypes.instanceOf(Exam),
  lessons: ImmutablePropTypes.listOf(Lesson).isRequired,
  track: ImmutablePropTypes.map.isRequired,
  trackIntroductionVideo: PropTypes.instanceOf(Video)
};

function scrollToCurrentContentOnMount(WrappedComponent) {
  return class extends Component {
    render() {
      return /*#__PURE__*/_jsx(WrappedComponent, Object.assign({}, this.props));
    }

  };
}

export default scrollToCurrentContentOnMount(ExpandedTrackNavigatorComponent);